import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, ArrowRight, Phone, CalendarDays, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  New: "chip-teal",
  "Follow-up": "chip-amber",
  Converted: "chip-green",
  Lost: "chip-pink",
};

const CLASSES = ["Playgroup", "Nursery", "LKG", "UKG"];
const SOURCES = ["Walk-in", "Referral", "Online", "Other"];
const STATUSES = ["New", "Follow-up", "Converted", "Lost"];

const emptyEnq = {
  parent_name: "", phone: "", child_name: "", child_age: "",
  source: "Walk-in", status: "New", follow_up_date: "", notes: "", interested_class: "Playgroup",
};

export default function Enquiries() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEnq);
  const [convertOpen, setConvertOpen] = useState(null);
  const [childForm, setChildForm] = useState(null);

  const load = async () => {
    const { data } = await api.get("/enquiries", { params: filter ? { status: filter } : {} });
    setItems(data);
  };
  useEffect(() => { load(); }, [filter]);

  const openNew = () => { setEditing(null); setForm(emptyEnq); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...e, child_age: e.child_age ?? "" }); setOpen(true); };

  const save = async () => {
    const payload = { ...form, child_age: form.child_age === "" ? null : Number(form.child_age) };
    try {
      if (editing) {
        await api.put(`/enquiries/${editing.id}`, payload);
        toast.success("Enquiry updated");
      } else {
        await api.post("/enquiries", payload);
        toast.success("Enquiry added");
      }
      setOpen(false);
      load();
      // trigger convert flow if just moved to Converted
      if (payload.status === "Converted") {
        promptConvert({ ...editing, ...payload });
      }
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const promptConvert = (enq) => {
    setConvertOpen(enq);
    setChildForm({
      name: enq.child_name || "",
      date_of_birth: "",
      parent_name: enq.parent_name,
      parent_name_2: "",
      parent_phone: enq.phone,
      parent_email: "",
      parent_occupation: "",
      admission_date: new Date().toISOString().slice(0, 10),
      class_name: enq.interested_class || "Playgroup",
      sibling_id: null,
      sibling_details: "",
      notes: "",
    });
  };

  const doConvert = async () => {
    try {
      await api.post(`/enquiries/${convertOpen.id}/convert`, childForm);
      toast.success(`${childForm.name} enrolled! 🎉`);
      setConvertOpen(null);
      load();
    } catch (e) {
      toast.error("Could not convert");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this enquiry?")) return;
    await api.delete(`/enquiries/${id}`);
    load();
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black">Enquiries</h1>
          <p className="text-muted-foreground font-medium">Every parent who reaches out to Kidzkulam.</p>
        </div>
        <Button
          data-testid="add-enquiry-btn"
          onClick={openNew}
          className="rounded-full h-12 px-6 font-bold shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all"
        >
          <Plus size={18} /> New Enquiry
        </Button>
      </header>

      <div className="flex flex-wrap gap-2">
        <button
          data-testid="filter-all"
          onClick={() => setFilter("")}
          className={`px-4 py-2 rounded-full font-semibold text-sm ${filter === "" ? "bg-primary text-primary-foreground" : "bg-muted"}`}
        >
          All
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`filter-${s.toLowerCase()}`}
            onClick={() => setFilter(s)}
            className={`px-4 py-2 rounded-full font-semibold text-sm ${filter === s ? "bg-primary text-primary-foreground" : "bg-muted"}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-muted-foreground font-medium">
            No enquiries yet. Add the first one!
          </div>
        )}
        {items.map((e) => (
          <Card key={e.id} data-testid={`enquiry-${e.id}`} className="card-soft rounded-3xl p-5 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-extrabold text-lg leading-tight">{e.parent_name}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Phone size={13} /> {e.phone}
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLE[e.status]}`}>{e.status}</span>
            </div>
            <div className="text-sm space-y-1">
              {e.child_name && <div><span className="text-muted-foreground">Child:</span> <b>{e.child_name}</b>{e.child_age ? ` · ${e.child_age} yrs` : ""}</div>}
              <div><span className="text-muted-foreground">Interested:</span> <b>{e.interested_class || "—"}</b> · <span className="text-muted-foreground">Source:</span> {e.source}</div>
              {e.follow_up_date && (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <CalendarDays size={13} /> Follow-up: {e.follow_up_date}
                </div>
              )}
              {e.notes && <div className="text-muted-foreground italic">"{e.notes}"</div>}
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                data-testid={`edit-enquiry-${e.id}`}
                variant="outline" size="sm" className="rounded-full flex-1"
                onClick={() => openEdit(e)}
              >
                Edit
              </Button>
              {e.status !== "Converted" && (
                <Button
                  data-testid={`convert-enquiry-${e.id}`}
                  size="sm" className="rounded-full flex-1"
                  onClick={() => promptConvert(e)}
                >
                  Convert <ArrowRight size={14} />
                </Button>
              )}
              <Button variant="ghost" size="sm" className="rounded-full text-destructive" onClick={() => del(e.id)}>
                Delete
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Enquiry" : "New Enquiry"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Parent Name</Label><Input data-testid="enq-parent" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="h-11 rounded-xl" /></div>
              <div><Label>Phone</Label><Input data-testid="enq-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 rounded-xl" /></div>
              <div><Label>Child Name</Label><Input value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} className="h-11 rounded-xl" /></div>
              <div><Label>Child Age</Label><Input type="number" step="0.1" value={form.child_age} onChange={(e) => setForm({ ...form, child_age: e.target.value })} className="h-11 rounded-xl" /></div>
              <div>
                <Label>Source</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="enq-status" className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Interested Class</Label>
                <Select value={form.interested_class} onValueChange={(v) => setForm({ ...form, interested_class: v })}>
                  <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Follow-up date</Label><Input type="date" value={form.follow_up_date || ""} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} className="h-11 rounded-xl" /></div>
            </div>
            <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="enq-save" onClick={save} className="rounded-full">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert dialog */}
      <Dialog open={!!convertOpen} onOpenChange={(v) => !v && setConvertOpen(null)}>
        <DialogContent className="rounded-3xl max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-primary" size={18} /> Convert to Child Enrollment
            </DialogTitle>
          </DialogHeader>
          {childForm && (
            <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Child Name</Label><Input value={childForm.name} onChange={(e) => setChildForm({ ...childForm, name: e.target.value })} className="h-11 rounded-xl" /></div>
                <div><Label>Date of Birth</Label><Input type="date" value={childForm.date_of_birth} onChange={(e) => setChildForm({ ...childForm, date_of_birth: e.target.value })} className="h-11 rounded-xl" /></div>
                <div><Label>Parent Name</Label><Input value={childForm.parent_name} onChange={(e) => setChildForm({ ...childForm, parent_name: e.target.value })} className="h-11 rounded-xl" /></div>
                <div><Label>Parent Phone</Label><Input value={childForm.parent_phone} onChange={(e) => setChildForm({ ...childForm, parent_phone: e.target.value })} className="h-11 rounded-xl" /></div>
                <div><Label>Parent Email</Label><Input value={childForm.parent_email} onChange={(e) => setChildForm({ ...childForm, parent_email: e.target.value })} className="h-11 rounded-xl" /></div>
                <div><Label>Occupation</Label><Input value={childForm.parent_occupation} onChange={(e) => setChildForm({ ...childForm, parent_occupation: e.target.value })} className="h-11 rounded-xl" /></div>
                <div>
                  <Label>Class</Label>
                  <Select value={childForm.class_name} onValueChange={(v) => setChildForm({ ...childForm, class_name: v })}>
                    <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Admission Date</Label><Input type="date" value={childForm.admission_date} onChange={(e) => setChildForm({ ...childForm, admission_date: e.target.value })} className="h-11 rounded-xl" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button data-testid="confirm-convert" onClick={doConvert} className="rounded-full">Enroll Child</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
